// Auto-generated by scripts/split_calculators.py — keeps deterministic oilfield formulas in per-category modules.
import { round, num, positive, toRad } from "./_common";
// eslint-disable-next-line no-unused-vars
const _imports_ok = { round, num, positive, toRad };

// ===== Category: workover =====
// --- Workover & Completion ---
// =========================================================================
// WORKOVER & COMPLETION
// =========================================================================

export function tubingStretchFree({ tubingWeight, length, area }) {
  // Free-hanging stretch under own weight: ΔL = (W × L) / (2 × E × A)  (axial gravity load)
  // Where W = weight per foot (lb/ft), L = length (ft), A = pipe area in², E = 30e6 psi
  // For uniform self-weight: stretch = (ρ × L²) / (2E); using W (lb/ft) and A:
  // Force at top from self-weight = W × L; average stress = W × L / (2A)
  // ΔL (in) = avg_stress × L_in / E = (W × L_ft / (2A)) × (L_ft × 12) / E
  const W = positive(num(tubingWeight, "Tubing Weight"), "Tubing Weight"); // lb/ft
  const L = positive(num(length, "Length"), "Length"); // ft
  const A = positive(num(area, "Pipe Cross Area"), "Pipe Cross Area"); // in²
  const E = 30e6; // psi
  const stretchIn = (W * L * (L * 12)) / (2 * A * E);
  return {
    result: round(stretchIn, 3),
    unit: "in",
    formula: "ΔL = (W·L·12·L) / (2·E·A);  E = 30×10⁶ psi",
    steps: [
      `W=${W} lb/ft, L=${L} ft, A=${A} in², E=30×10⁶ psi`,
      `ΔL = (${W} × ${L} × 12 × ${L}) / (2 × ${A} × 30×10⁶)`,
      `ΔL = ${round(stretchIn, 3)} in`,
    ],
    inputs: { "W (lb/ft)": W, "L (ft)": L, "A (in²)": A },
  };
}

export function tubingStretchHookLoad({ pullForce, length, area }) {
  // Hooke's law stretch under applied tension at top: ΔL = (F × L) / (A × E)
  const F = positive(num(pullForce, "Pull Force"), "Pull Force"); // lbf
  const L = positive(num(length, "Length"), "Length"); // ft
  const A = positive(num(area, "Pipe Cross Area"), "Pipe Cross Area"); // in²
  const E = 30e6; // psi
  const stretchIn = (F * L * 12) / (A * E);
  return {
    result: round(stretchIn, 3),
    unit: "in",
    formula: "ΔL = (F·L·12) / (A·E);  E = 30×10⁶ psi",
    steps: [
      `F=${F} lbf, L=${L} ft, A=${A} in², E=30×10⁶ psi`,
      `ΔL = (${F} × ${L} × 12) / (${A} × 30×10⁶) = ${round(stretchIn, 3)} in`,
    ],
    inputs: { "F (lbf)": F, "L (ft)": L, "A (in²)": A },
  };
}

export function bhpFromWHP({ whp, mw, tvd }) {
  const W = num(whp, "WHP"); if (W < 0) throw new Error("WHP cannot be negative");
  const M = positive(num(mw, "Fluid Weight"), "Fluid Weight");
  const D = positive(num(tvd, "TVD"), "TVD");
  const hp = 0.052 * M * D;
  const bhp = W + hp;
  return {
    result: round(bhp, 0),
    unit: "psi (BHP)",
    formula: "BHP = WHP + 0.052 × MW × TVD",
    steps: [
      `Hydrostatic = 0.052 × ${M} × ${D} = ${round(hp, 0)} psi`,
      `BHP = ${W} + ${round(hp, 0)} = ${round(bhp, 0)} psi`,
    ],
    inputs: { "WHP (psi)": W, "Fluid Wt (ppg)": M, "TVD (ft)": D },
  };
}

export function killFluidWeightWorkover({ reservoirPressure, tvd, safetyMargin }) {
  const P = positive(num(reservoirPressure, "Reservoir Pressure"), "Reservoir Pressure");
  const D = positive(num(tvd, "TVD"), "TVD");
  const S = num(safetyMargin || 0, "Safety Margin");
  if (S < 0) throw new Error("Safety margin must be ≥ 0");
  const required = P / (0.052 * D);
  const total = required + S;
  return {
    result: round(total, 2),
    unit: "ppg",
    formula: "MW_kill = P_res / (0.052 × TVD) + safety",
    steps: [
      `Base = ${P} / (0.052 × ${D}) = ${round(required, 3)} ppg`,
      `With safety ${S} ppg → ${round(total, 2)} ppg`,
    ],
    inputs: { "P_res (psi)": P, "TVD (ft)": D, "Safety (ppg)": S },
  };
}

export function perforationPressure({ surfacePressure, mw, tvd, frictionLoss }) {
  const P = num(surfacePressure, "Surface Pressure"); if (P < 0) throw new Error("Surface pressure must be ≥ 0");
  const M = positive(num(mw, "Fluid Weight"), "Fluid Weight");
  const D = positive(num(tvd, "TVD"), "TVD");
  const F = num(frictionLoss || 0, "Friction Loss"); if (F < 0) throw new Error("Friction loss must be ≥ 0");
  const hp = 0.052 * M * D;
  const perf = P + hp - F;
  return {
    result: round(perf, 0),
    unit: "psi",
    formula: "P_perf = P_surface + 0.052·MW·TVD − friction",
    steps: [
      `Hydrostatic = 0.052 × ${M} × ${D} = ${round(hp, 0)} psi`,
      `P_perf = ${P} + ${round(hp, 0)} − ${F} = ${round(perf, 0)} psi`,
    ],
    inputs: { "P_surface (psi)": P, "MW (ppg)": M, "TVD (ft)": D, "Friction (psi)": F },
  };
}

